<template>
  <div class="home">
    <div>
      <section>
        <div class="py-8 px-8">
          <div class="d-flex flex-wrap align-items-center justify-content-between">
            <div class="col-12 col-lg-auto mb-3 mb-lg-0">
              <div class="d-flex align-items-center">
                <h4 class="mb-0 me-2">{{$t('my_planning')}}</h4>
              </div>
            </div>
            <div class="col-12 col-lg-auto">
              <div class="d-flex align-items-center gap-2">
                <div class="input-group flex-grow-1" style="border-bottom: 1px solid #ddd;">
                  <input class="form-control border-0 search-input" autocomplete="off" type="text" v-model="search" :placeholder="$t('search')" @input="onSearchInput">
                </div>
                <button
                  type="button"
                  class="btn btn-outline-secondary d-md-none"
                  :aria-label="$t('filter')"
                  :title="$t('filter')"
                  :aria-expanded="showFilters ? 'true' : 'false'"
                  @click="showFilters = !showFilters"
                >
                  <AppIcon name="bi-funnel" class="bi bi-funnel" aria-hidden="true" />
                  <span v-if="activeFilterCount" class="badge bg-secondary ms-1">{{ activeFilterCount }}</span>
                </button>
              </div>

              <div class="d-md-none mt-2">
                <select v-model="dateRangeOption" @change="handleDateRangeChange" class="form-select form-select-sm">
                  <option value="">{{$t('filter_by_date')}}</option>
                  <option value="today">{{$t('today')}}</option>
                  <option value="yesterday">{{$t('yesterday')}}</option>
                  <option value="this_week">{{$t('this_week')}}</option>
                  <option value="last_week">{{$t('last_week')}}</option>
                  <option value="this_month">{{$t('this_month')}}</option>
                  <option value="custom">{{$t('custom_date')}}</option>
                </select>
                <date-picker
                  v-if="dateRangeOption === 'custom'"
                  :clearable="true"
                  :editable="false"
                  class="w-100 mt-2"
                  v-model="range"
                  type="date"
                  range
                  :multi-calendars="1"
                  :lang="datePickerLocale"
                  :placeholder="$t('select_date_range')"
                ></date-picker>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- table/content -->
      <section>
        <div class="px-8">
          <!-- Filters section (same as Product.vue) -->
          <div class="row align-items-end g-2 mb-4" :class="{'d-none d-md-flex': !showFilters}">
            
            <!-- Filter Dropdown (Shopify Style) -->
            <div class="col-12 col-md-auto">
              <div class="dropdown">
                <button class="btn btn-outline-secondary dropdown-toggle btn-sm" type="button" id="filterDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                  <AppIcon name="bi-funnel" class="bi bi-funnel" /> {{$t('filter')}}
                  <span v-if="activeFilters.length" class="badge bg-secondary ms-1">{{activeFilters.length}}</span>
                </button>
                <button v-if="activeFilters.length" class="btn btn-link btn-sm text-danger text-decoration-none ms-2" type="button" @click.stop="clearAllFilters">
                  <AppIcon name="bi-x-circle" class="bi bi-x-circle me-1" />{{$t('clear_all')}}
                </button>
                <div class="dropdown-menu p-3" aria-labelledby="filterDropdown" style="min-width: 300px;" @click.stop>
                  
                  <!-- Active Filters -->
                  <div v-if="activeFilters.length > 0" class="mb-3">
                    <div v-for="(filter, index) in activeFilters" :key="index" class="mb-2">
                      <label class="form-label small mb-1">{{$t(filter.key)}}</label>
                      <div class="input-group input-group-sm">
                        <!-- Select type filter -->
                        <select v-if="filter.type === 'select'" v-model="filter.value" class="form-select form-select-sm">
                          <option value="">{{$t('all')}}</option>
                          <option v-for="opt in filter.options" :key="opt.value" :value="opt.value">{{$t(opt.label)}}</option>
                        </select>
                        <!-- Text type filter -->
                        <input v-else type="text" v-model="filter.value" class="form-control form-control-sm" :placeholder="$t(filter.key)">
                        <button class="btn btn-outline-danger" type="button" @click="removeFilter(index)">
                          <AppIcon name="bi-trash" class="bi bi-trash" />
                        </button>
                      </div>
                    </div>
                    <hr class="dropdown-divider">
                  </div>

                  <!-- Add Filter Options -->
                  <div class="d-grid gap-2">
                    <button v-for="filter in selectableFilters" :key="filter.key" 
                            class="btn btn-sm btn-outline-primary text-start" 
                            @click="addFilter(filter)">
                      <AppIcon name="bi-plus" class="bi bi-plus" /> {{$t(filter.key)}}
                    </button>
                  </div>

                  <!-- Apply and Clear -->
                  <div class="d-flex justify-content-between align-items-center mt-3">
                    <button class="btn btn-sm btn-success" @click="applyFilters">
                      {{$t('apply')}}
                    </button>
                    <button v-if="activeFilters.length > 0" class="btn btn-link btn-sm text-decoration-none text-danger p-0" @click="clearAllFilters">
                      {{$t('clear_all')}}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Spacer for desktop -->
            <div class="col d-none d-md-block"></div>
            
            <!-- Date picker -->
            <div class="d-none d-md-block col-md-3 col-lg-2">
              <select v-model="dateRangeOption" @change="handleDateRangeChange" class="form-select form-select-sm">
                <option value="">{{$t('filter_by_date')}}</option>
                <option value="today">{{$t('today')}}</option>
                <option value="yesterday">{{$t('yesterday')}}</option>
                <option value="this_week">{{$t('this_week')}}</option>
                <option value="last_week">{{$t('last_week')}}</option>
                <option value="this_month">{{$t('this_month')}}</option>
                <option value="custom">{{$t('custom_date')}}</option>
              </select>
            </div>
            <div class="d-none d-md-block col-md-3 col-lg-2" v-if="dateRangeOption === 'custom'">
              <date-picker :clearable="true" :editable="false" class="w-100" v-model="range" type="date" range :multi-calendars="1" :lang="datePickerLocale" :placeholder="$t('select_date_range')"></date-picker>
            </div>

          </div>
        </div>
      </section>

      <section class="px-8 pb-8">
        <div class="my-planning-content">
          <!-- Loading -->
          <table-loading-bar
            v-if="loading && currentView !== 'calendar' && currentView !== 'list'"
            class="my-planning-view-state text-center"
          />

          <!-- Keep the calendar mounted while its month data is loading. Unmounting
               it here causes its mounted hook to emit month-change repeatedly. -->
          <div v-if="currentView === 'calendar'">
            <my-planning-calendar
              :plannings="filteredPlannings"
              @open-detail="openDetailModal"
              @month-change="handleCalendarMonthChange"
            ></my-planning-calendar>
          </div>

          <!-- List View -->
          <div v-if="currentView === 'list'" class="page-table-container my-planning-list-table mb-6 rounded bg-white shadow-sm">
            <div class="table-responsive">
              <table class="table mb-0 table-borderless table-striped small">
                <thead>
                  <tr class="text-secondary">
                    <th class="pt-5 pb-4 px-8" style="width: 40px;">
                      <div class="form-check mb-0">
                        <input class="form-check-input" type="checkbox" :checked="areAllSelected" @change="toggleSelectAll">
                      </div>
                    </th>
                    <sortable-table-header v-if="visibleColumns.id" field="id" label="id" :sort-field="sortField" :sort-direction="sortDirection" header-class="pt-5 pb-4 px-8" @sort="toggleSort" />
                    <sortable-table-header v-if="visibleColumns.title" field="title" label="title" :sort-field="sortField" :sort-direction="sortDirection" header-class="pt-5 pb-4 px-8" @sort="toggleSort" />
                    <sortable-table-header v-if="visibleColumns.client" field="client" label="client" :sort-field="sortField" :sort-direction="sortDirection" header-class="pt-5 pb-4 px-8" @sort="toggleSort" />
                    <sortable-table-header v-if="visibleColumns.location" field="location" label="location" :sort-field="sortField" :sort-direction="sortDirection" header-class="pt-5 pb-4 px-8" @sort="toggleSort" />
                    <sortable-table-header v-if="visibleColumns.time" field="time" label="time" :sort-field="sortField" :sort-direction="sortDirection" header-class="pt-5 pb-4 px-8" @sort="toggleSort" />
                    <sortable-table-header v-if="visibleColumns.status" field="status" label="status" :sort-field="sortField" :sort-direction="sortDirection" header-class="pt-5 pb-4 px-8" @sort="toggleSort" />
                    <sortable-table-header v-if="visibleColumns.created_at" field="created_at" label="created_at" :sort-field="sortField" :sort-direction="sortDirection" header-class="pt-5 pb-4 px-8" @sort="toggleSort" />
                    <th v-if="visibleColumns.action" class="pt-5 pb-4 px-8">
                      <div class="d-flex align-items-center justify-content-between">
                        <span>{{$t('action')}}</span>
                        <div class="dropdown">
                          <button class="btn btn-sm btn-link p-0 ms-2" type="button" data-bs-toggle="dropdown" aria-expanded="false" :title="$t('columns')" :aria-label="$t('columns')">
                            <AppIcon name="bi-three-dots-vertical" class="bi bi-three-dots-vertical" />
                          </button>
                          <ul class="dropdown-menu dropdown-menu-end p-2" style="min-width: 200px; max-height: 300px; overflow-y: auto;">
                            <li v-for="(val, key) in visibleColumns" :key="key">
                              <div class="form-check dropdown-item">
                                <input class="form-check-input" type="checkbox" v-model="visibleColumns[key]" :id="'my-col-' + key">
                                <label class="form-check-label w-100" :for="'my-col-' + key">
                                  {{$t(key)}}
                                </label>
                              </div>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="p in filteredPlannings" :key="p.id" :class="{'table-active': p.selected}" @click="openDetailModal(p)" style="cursor: pointer;">
                    <td class="py-6 px-8">
                      <div class="form-check mb-0">
                        <input class="form-check-input" type="checkbox" v-model="p.selected" @change="changeSelectedItem">
                      </div>
                    </td>
                    <td v-if="visibleColumns.id" class="py-6 px-8" style="text-decoration: underline">
                      #{{ p.id.toString().substring(0, 8) }}
                    </td>
                    <td v-if="visibleColumns.title" class="py-6 px-8">
                      <strong>{{ p.title }}</strong>
                      <div v-if="p.description" class="text-muted small text-truncate" style="max-width: 200px;">{{ p.description }}</div>
                    </td>
                    <td v-if="visibleColumns.client" class="py-6 px-8">
                      <span v-if="p.client && p.client.name">{{ p.client.name }}</span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td v-if="visibleColumns.location" class="py-6 px-8">
                      <div v-if="p.address" class="text-muted small text-truncate" style="max-width: 150px;">{{ p.address }}</div>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td v-if="visibleColumns.time" class="py-6 px-8">
                      {{ p.start_time || '--:--' }} - {{ p.end_time || '--:--' }}
                    </td>
                    <td v-if="visibleColumns.status" class="py-6 px-8">
                      <span :class="'badge bg-' + getStatusColor(p.status)">{{ $t(p.status) }}</span>
                    </td>
                    <td v-if="visibleColumns.created_at" class="py-6 px-8">
                      {{ helper.formatDate(p.created_at) }}
                    </td>
                    <td v-if="visibleColumns.action" class="py-6 px-8">
                      <button v-if="p.status === 'planned'" @click="updateStatus(p, 'in_progress')" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-primary btn-xs">
                        <AppIcon name="bi-play-fill" class="bi bi-play-fill" /> {{$t('start_task')}}
                      </button>
                      <button v-if="p.status === 'in_progress'" @click.stop="showCompletionModal(p)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-success btn-xs">
                        <AppIcon name="bi-check-lg" class="bi bi-check-lg" /> {{$t('mark_completed')}}
                      </button>
                      <button v-if="p.status === 'in_progress'" @click="updateStatus(p, 'failed')" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-danger btn-xs">
                        <AppIcon name="bi-x-lg" class="bi bi-x-lg" />
                      </button>
                      <span v-if="p.status === 'completed'" class="text-success small fw-bold"><AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill me-1" />{{$t('completed')}}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <table-loading-bar v-if="loading" class="page-table-state text-center" />
            <div v-if="!loading && filteredPlannings.length === 0" class="page-table-state text-center">
              {{$t('no_plannings_for_today')}}.
            </div>
          </div>

          <!-- Empty state for card and kanban views -->
          <div
            v-if="!loading && (currentView === 'cards' || currentView === 'kanban') && filteredPlannings.length === 0"
            class="my-planning-view-state text-center"
          >
            {{$t('no_plannings_for_today')}}.
          </div>

          <!-- Kanban View -->
          <div v-if="!loading && currentView === 'kanban' && filteredPlannings.length > 0">
            <my-planning-kanban 
              :plannings="filteredPlannings" 
              @update-status="updateStatus"
              @open-detail="openDetailModal"
            ></my-planning-kanban>
          </div>

          <!-- Cards View -->
          <div v-if="!loading && currentView === 'cards' && filteredPlannings.length > 0" class="row g-4 mb-6">
            <div v-for="p in filteredPlannings" :key="p.id" class="col-12 col-md-6 col-lg-4">
              <div class="card border-0 shadow-sm transition-hover h-100 bg-white overflow-hidden" style="border-radius: 12px; cursor: pointer;" @click="openDetailModal(p)">
                <div :class="'status-bar bg-' + getStatusColor(p.status)" style="height: 4px;"></div>
                <div class="card-body p-4">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <span class="text-muted small fw-bold">#{{ p.id.toString().substring(0, 8) }}</span>
                    <span :class="'badge rounded-pill px-3 py-2 bg-' + getStatusColor(p.status) + '-soft text-' + getStatusColor(p.status)">
                      {{ $t(p.status) }}
                    </span>
                  </div>
                  
                  <h5 class="fw-bold mb-2 text-dark">{{ p.title }}</h5>
                  <p v-if="p.description" class="text-muted small mb-4 line-clamp-2">{{ p.description }}</p>

                  <div class="spacing-list mb-4">
                    <div v-if="p.client && p.client.name" class="d-flex align-items-center mb-2">
                      <div class="icon-circle bg-light me-3">
                        <AppIcon name="bi-person" class="bi bi-person text-secondary" />
                      </div>
                      <div class="flex-grow-1">
                        <small class="text-muted d-block" style="font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.5px;">{{$t('client')}}</small>
                        <span class="text-dark fw-medium small">{{ p.client.name }}</span>
                      </div>
                    </div>

                    <div class="d-flex align-items-center mb-2">
                      <div class="icon-circle bg-light me-3">
                        <AppIcon name="bi-calendar3" class="bi bi-calendar3 text-secondary" />
                      </div>
                      <div class="flex-grow-1">
                        <small class="text-muted d-block" style="font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.5px;">{{$t('date')}}</small>
                        <span class="text-dark fw-medium small">{{ helper.formatDate(p.date) }}</span>
                      </div>
                    </div>

                    <div class="d-flex align-items-center mb-2">
                      <div class="icon-circle bg-light me-3">
                        <AppIcon name="bi-clock" class="bi bi-clock text-secondary" />
                      </div>
                      <div class="flex-grow-1">
                        <small class="text-muted d-block" style="font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.5px;">{{$t('time')}}</small>
                        <span class="text-dark fw-medium small">{{ p.start_time || '--:--' }} - {{ p.end_time || '--:--' }}</span>
                      </div>
                    </div>

                    <div v-if="p.address" class="d-flex align-items-center mb-2">
                      <div class="icon-circle bg-light me-3">
                        <AppIcon name="bi-geo-alt" class="bi bi-geo-alt text-secondary" />
                      </div>
                      <div class="flex-grow-1">
                        <small class="text-muted d-block" style="font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.5px;">{{$t('address')}}</small>
                        <span class="text-dark fw-medium small text-truncate d-block" style="max-width: 200px;">{{ p.address }}</span>
                      </div>
                    </div>
                  </div>

                  <div v-if="p.instructions" class="instruction-box p-3 bg-light rounded mb-4 border-start border-4 border-primary">
                    <small class="text-primary fw-bold d-block mb-1" style="font-size: 0.65rem; text-transform: uppercase;">{{$t('instructions')}}</small>
                    <p class="mb-0 small text-dark">{{ p.instructions }}</p>
                  </div>

                  <div class="mt-auto d-flex gap-2 pt-2">
                    <button v-if="p.status === 'planned'" @click="updateStatus(p, 'in_progress')" class="btn btn-primary d-flex align-items-center justify-content-center gap-2 w-100 py-2 rounded-3 shadow-sm border-0">
                      <AppIcon name="bi-play-fill" class="bi bi-play-fill fs-5" />
                      <span class="fw-bold">{{$t('start_task')}}</span>
                    </button>
                    
                    <button v-if="p.status === 'in_progress'" @click.stop="showCompletionModal(p)" class="btn btn-success d-flex align-items-center justify-content-center gap-2 flex-grow-1 py-2 rounded-3 shadow-sm border-0">
                      <AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill" />
                      <span class="fw-bold">{{$t('mark_completed')}}</span>
                    </button>
                    
                    <button v-if="p.status === 'in_progress'" @click="updateStatus(p, 'failed')" class="btn btn-outline-danger btn-icon py-2 px-3 rounded-3">
                      <AppIcon name="bi-x-lg" class="bi bi-x-lg" />
                    </button>
                    
                    <div v-if="p.status === 'completed'" class="completed-badge w-100 py-2 rounded-3 text-center">
                      <AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill me-2 text-success" />
                      <span class="text-success fw-bold small">{{$t('task_completed')}}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Bulk Action Bar -->
    <div v-if="isSelectedItem" class="position-fixed bottom-0 start-50 translate-middle-x mb-4 p-3 bg-white shadow rounded d-flex align-items-center gap-3" style="z-index: 1050; border: 1px solid #dee2e6; min-width: 300px; justify-content: space-between;">
        <div class="d-flex align-items-center">
            <span class="fw-bold me-2">{{ selectedCount }}</span>
            <span class="text-muted">{{$t('selected')}}</span>
        </div>
        <div class="d-flex gap-2">
            <button class="btn btn-danger btn-sm d-flex align-items-center" @click="deleteSelected">
                <AppIcon name="bi-trash" class="bi bi-trash me-1" /> {{$t('delete')}}
            </button>
        </div>
    </div>

    <!-- Completion Modal -->
    <div v-if="completionModal.show" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{$t('confirm_completion')}}</h5>
            <button type="button" class="btn-close" @click="completionModal.show = false"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted small mb-3">{{$t('add_feedback_for_manager')}}</p>
            <textarea v-model="completionModal.feedback" class="form-control mb-3" rows="4" :placeholder="$t('write_feedback_placeholder')"></textarea>

            <div class="mb-3">
              <label class="form-label small fw-bold text-muted">{{$t('add_photos')}}</label>
              <div class="d-flex flex-wrap gap-2 mb-2">
                <div v-for="(img, index) in completionModal.images" :key="index" class="position-relative">
                  <img :src="img" class="rounded border" style="width: 80px; height: 80px; object-fit: cover;">
                  <button class="btn btn-danger btn-sm rounded-circle position-absolute top-0 end-0 p-0 d-flex align-items-center justify-content-center" style="width: 20px; height: 20px; margin: -5px;" @click="removeImage(index)">
                    <AppIcon name="bi-x" class="bi bi-x" style="font-size: 14px;" />
                  </button>
                </div>
                <div v-if="uploading" class="rounded bg-light d-flex align-items-center justify-content-center" style="width: 80px; height: 80px;">
                  <div class="spinner-border spinner-border-sm text-primary"></div>
                </div>
                <label v-if="completionModal.images.length < 5 && !uploading" class="rounded bg-light d-flex align-items-center justify-content-center" style="width: 80px; height: 80px; border: 2px dashed #dee2e6; cursor: pointer;">
                  <AppIcon name="bi-camera" class="bi bi-camera fs-4 text-muted" />
                  <input type="file" class="d-none" accept="image/*" @change="uploadImage">
                </label>
              </div>
              <small class="text-muted">{{$t('max_5_photos')}}</small>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="completionModal.show = false">{{$t('cancel')}}</button>
            <button type="button" class="btn btn-success" @click="submitCompletion">{{$t('submit')}}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Planning Detail Modal -->
    <planning-detail-modal
      v-if="selectedPlanning"
      :planning="selectedPlanning"
      @close="selectedPlanning = null"
    ></planning-detail-modal>
  </div>
</template>

<script>
import helper from '../../helper'
import moment from 'moment'
import MyPlanningKanban from '../../components/planning/MyPlanningKanban'
import MyPlanningCalendar from '../../components/planning/MyPlanningCalendar'
import PlanningDetailModal from '../../components/planning/PlanningDetailModal'
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/fr';
import 'vue2-datepicker/locale/ar';
import sortableList from '../../mixins/sortableList'

export default {
  name: 'MyPlanning',
  persistedFilterState: {
    key: 'my-planning',
    fields: ['search', 'activeFilters', 'dateRangeOption', 'range', 'start_date', 'end_date', 'sortField', 'sortDirection'],
    dateFields: ['range'],
  },
  mixins: [sortableList],
  components: {
    MyPlanningKanban,
    MyPlanningCalendar,
    PlanningDetailModal,
    DatePicker
  },
  data() {
    return {
      helper,
      loading: false,
      uploading: false,
      plannings: [],
      search: '',
      range: [moment().startOf('isoWeek').toDate(), moment().endOf('isoWeek').toDate()],
      start_date: moment().startOf('isoWeek').format('YYYY-MM-DD'),
      end_date: moment().endOf('isoWeek').format('YYYY-MM-DD'),
      dateRangeOption: 'this_week',
      currentView: 'cards',
      showFilters: false,
      activeFilters: [],
      availableFilters: [
        {
          key: 'status',
          type: 'select',
          options: [
            { value: 'planned', label: 'planned' },
            { value: 'in_progress', label: 'in_progress' },
            { value: 'completed', label: 'completed' },
            { value: 'failed', label: 'failed' },
            { value: 'postponed', label: 'postponed' }
          ]
        },
        { key: 'title' },
        { key: 'address' },
        {
          key: 'client_id',
          type: 'select',
          options: [] // Filled in mounted
        }
      ],
      completionModal: {
        show: false,
        planning: null,
        feedback: '',
        images: []
      },
      visibleColumns: {
        id: true,
        title: true,
        client: true,
        location: true,
        time: true,
        status: true,
        created_at: true,
        action: true
      },
      isSelectedItem: false,
      selectedPlanning: null,
      calendarRange: null,
      refreshRequestId: 0
    }
  },
  computed: {
    datePickerLocale() {
      return ['fr', 'ar'].includes(this.$i18n.locale) ? this.$i18n.locale : 'en'
    },
    dateRangeLabel() {
      if (this.dateRangeOption === 'custom' && this.start_date && this.end_date) {
        return `${this.start_date} - ${this.end_date}`
      }
      return this.$t(this.dateRangeOption) || this.$t('filter_by_date')
    },
    selectableFilters() {
      return this.availableFilters.filter(f => !this.isFilterActive(f.key));
    },
    activeFilterCount() {
      return this.activeFilters.filter(f => f.value && f.value.toString().trim()).length;
    },
    areAllSelected() {
      return this.filteredPlannings.length > 0 && this.filteredPlannings.every(p => p.selected);
    },
    selectedCount() {
      return this.filteredPlannings.filter(p => p.selected).length;
    },
    filteredPlannings() {
      return this.sortRowsBy(this.plannings, planning => {
        const values = {
          client: planning.client?.name,
          location: planning.address,
          time: planning.start_time
        }
        return Object.prototype.hasOwnProperty.call(values, this.sortField)
          ? values[this.sortField]
          : planning[this.sortField]
      }, this.sortDirection)
    },
  },
  watch: {
    currentView(val, previousView) {
      localStorage.setItem('my_planning_view', val)
      if (previousView === 'calendar' && val !== 'calendar') {
        this.calendarRange = null
        this.refresh()
      }
    },
    range() {
      if (!this.range || this.range[0] == null) {
        this.start_date = null
        this.end_date = null
        this.refresh()
      } else if (this.range.length && this.range[0] && this.range[1]) {
        this.start_date = moment(this.range[0]).format('YYYY-MM-DD')
        this.end_date = moment(this.range[1]).format('YYYY-MM-DD')
        this.refresh()
      }
    }
  },
  mounted() {
    //if(helper.checkAccess('view_my_planning') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_my_planning') == -2) return this.$router.push({path : '/subscribe'})

    window.mixpanel.track("my_planning");
    
    // Keep My Planning on the cards view while the view switcher is disabled.
    this.currentView = 'cards'
    localStorage.setItem('my_planning_view', 'cards')

    this.refresh();
    this.getClients();
  },
  methods: {
    async refresh() {
      const requestId = ++this.refreshRequestId
      this.loading = true
      try {
        const activeRange = this.currentView === 'calendar' && this.calendarRange
          ? this.calendarRange
          : { start_date: this.start_date, end_date: this.end_date }
        const params = {
          start_date: activeRange.start_date,
          end_date: activeRange.end_date
        }

        // Add search if it exists
        if (this.search) {
          params.keyword = this.search;
        }

        // Add active filters to params
        this.activeFilters.forEach(filter => {
          if (filter.value && filter.value.toString().trim() !== '') {
            params[filter.key] = filter.value;
          }
        });
        
        const response = await helper.customGet(`${process.env.VUE_APP_API}/my-plannings`, {
          params
        })
        if (requestId !== this.refreshRequestId) return

        this.plannings = response.data || []
        
        // Add selected property
        this.plannings.forEach(p => {
          this.$set(p, 'selected', false);
        });
      } catch (error) {
        if (requestId === this.refreshRequestId) alert(this.$t('error_loading_my_planning'))
      } finally {
        if (requestId === this.refreshRequestId) this.loading = false
      }
    },
    async getClients() {
      try {
        const res = await helper.customGet(`${process.env.VUE_APP_API}/clients`, { params: { per_page: 200 } })
        const clientFilter = this.availableFilters.find(f => f.key === 'client_id')
        if (clientFilter) {
          clientFilter.options = (res.data.data || []).map(c => ({ value: c.id, label: c.name }))
        }
      } catch (e) { alert(e) }
    },
    async updateStatus(p, status) {
      if (status === 'completed') {
        this.showCompletionModal(p)
        return
      }
      try {
        await helper.customPatch(`${process.env.VUE_APP_API}/plannings/${p.id}/status`, { status }, { showToast: true })
        this.refresh()
      } catch (error) {
        alert(this.$t('error_updating_planning_status'))
      }
    },
    showCompletionModal(p) {
      this.completionModal.planning = p
      this.completionModal.feedback = ''
      this.completionModal.images = []
      this.completionModal.show = true
    },
    async uploadImage(event) {
      const file = event.target.files[0]
      if (!file) return

      this.uploading = true
      try {
        const businessId = this.$store.getters.BUSINESS.id
        const url = await helper.customUploadFile(file, `businesses/${businessId}/planning/${this.completionModal.planning.id}/${helper.generateRandString(20)}`, {
          entity_type: 'planning',
          entity_id: this.completionModal.planning.id
        })
        this.completionModal.images.push(url)
      } catch (error) {
        alert(this.$t('error_uploading_image'))
      } finally {
        this.uploading = false
      }
    },
    removeImage(index) {
      this.completionModal.images.splice(index, 1)
    },
    async submitCompletion() {
      if (!this.completionModal.planning) return
      try {
        await helper.customPatch(`${process.env.VUE_APP_API}/plannings/${this.completionModal.planning.id}/status`, {
          status: 'completed',
          feedback: this.completionModal.feedback
        }, { showToast: true })
        this.completionModal.show = false
        this.refresh()
      } catch (error) {
        alert(this.$t('error_submitting_completion'))
      }
    },
    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.refresh();
      }, 500);
    },
    addFilter(filter) {
        if (!this.isFilterActive(filter.key)) {
            const newFilter = { 
                key: filter.key, 
                value: '' 
            };
            if (filter.type) {
                newFilter.type = filter.type;
                newFilter.options = filter.options;
            }
            this.activeFilters.push(newFilter);
        }
    },
    removeFilter(index) {
        this.activeFilters.splice(index, 1);
        this.refresh();
    },
    isFilterActive(key) {
        return this.activeFilters.some(f => f.key === key);
    },
    applyFilters() {
        this.refresh();
    },
    clearAllFilters() {
        this.activeFilters = [];
        this.refresh();
    },
    // Bulk selection
    toggleSelectAll() {
      const newState = !this.areAllSelected;
      this.filteredPlannings.forEach(p => this.$set(p, 'selected', newState));
      this.changeSelectedItem();
    },
    changeSelectedItem() {
      this.isSelectedItem = this.filteredPlannings.find(p => p.selected === true) ? true : false
    },
    async deleteSelected() {
      const selectedIds = this.filteredPlannings.filter(p => p.selected).map(p => p.id);
      if (selectedIds.length === 0) return;

      const result = await this.$swal.fire({
        title: `${this.$t('are_you_sure')}?`,
        text: this.$t('you_cant_revert'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('yes_delete_it')
      });

      if (result.isConfirmed) {
        this.loading = true;
        try {
          const results = await Promise.allSettled(selectedIds.map(id => helper.customDelete(`${process.env.VUE_APP_API}/plannings/${id}`)));
          const deletedIds = helper.fulfilledRecordIds(selectedIds, results);
          this.plannings = helper.withoutDeletedRecords(this.plannings, deletedIds);
          if (deletedIds.length !== selectedIds.length) throw results.find(result => result.status === 'rejected').reason;
          this.isSelectedItem = false;
        } catch (e) {
          /* ignore */
        } finally {
          this.loading = false;
        }
      }
    },
    handleDateRangeChange() {
      if (this.dateRangeOption === '') {
        this.range = []
        this.start_date = null
        this.end_date = null
        this.refresh()
      } else if (this.dateRangeOption === 'today') {
        this.range = [moment().toDate(), moment().toDate()]
      } else if (this.dateRangeOption === 'yesterday') {
        this.range = [moment().subtract(1, 'days').toDate(), moment().subtract(1, 'days').toDate()]
      } else if (this.dateRangeOption === 'this_week') {
        this.range = [moment().startOf('isoWeek').toDate(), moment().endOf('isoWeek').toDate()]
      } else if (this.dateRangeOption === 'last_week') {
        this.range = [moment().subtract(1, 'weeks').startOf('isoWeek').toDate(), moment().subtract(1, 'weeks').endOf('isoWeek').toDate()]
      } else if (this.dateRangeOption === 'this_month') {
        this.range = [moment().startOf('month').toDate(), moment().endOf('month').toDate()]
      } else if (this.dateRangeOption === 'custom') {
        this.range = []
      }
    },
    goToToday() {
      this.dateRangeOption = 'today'
      this.handleDateRangeChange()
    },
    handleCalendarMonthChange(range) {
      this.calendarRange = range
      this.refresh()
    },
    getPriorityColor(priority) {
      const colors = { low: 'info', medium: 'primary', high: 'warning', urgent: 'danger' }
      return colors[priority] || 'primary'
    },
    getStatusColor(status) {
      const colors = {
        planned: 'primary',
        in_progress: 'info',
        completed: 'success',
        failed: 'danger',
        postponed: 'warning'
      }
      return colors[status] || 'secondary'
    },
    openDetailModal(planning) {
      this.selectedPlanning = planning
    }
  },

}
</script>

<style scoped>
.my-planning-content {
  min-height: 70vh;
  position: relative;
  margin-top: 1.5rem;
}

.my-planning-list-table,
.my-planning-view-state {
  min-height: 70vh;
}

.my-planning-view-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background-color: #fff;
  border-radius: 0.375rem;
}

.advanced-search-section {
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}
.filter-input {
  font-size: 0.875rem;
}

.transition-hover {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.transition-hover:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
}

.icon-circle {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bg-primary-soft { background-color: #e7f1ff; }
.bg-success-soft { background-color: #e9fbf0; }
.bg-info-soft { background-color: #e7faff; }
.bg-warning-soft { background-color: #fff8e6; }
.bg-danger-soft { background-color: #ffeef0; }

.text-primary { color: #0d6efd !important; }
.text-success { color: #198754 !important; }
.text-info { color: #0dcaf0 !important; }
.text-warning { color: #ffc107 !important; }
.text-danger { color: #dc3545 !important; }

.completed-badge {
  background-color: #f0fff4;
  border: 1px solid #c6f6d5;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
